import { makeModuleInstance } from '@zg-rentals/util';

export type Tracer = {
  makeTraceID(): string;
};

export type TraceHeaders = { traceparent: string; tracestate?: string };

const { get: getTraceHeaderProvider, set: setTraceHeaderProvider } = makeModuleInstance<
  (() => TraceHeaders | undefined) | undefined
>(undefined);

export { setTraceHeaderProvider };

/** W3C trace headers for the current context; undefined until a tracer registers a provider. */
export function getTraceHeaders(): TraceHeaders | undefined {
  return getTraceHeaderProvider()?.();
}

export const TRACE_COOKIE_NAME = 'rjs-trace';
export const TRACE_COOKIE_DURATION_SEC = 31536000;
export const SESSION_ID_LENGTH = 26;
export const TRACE_ID_LENGTH = 32;
export const SPAN_ID_LENGTH = 16;

export type Traceparent = {
  traceId: string;
  spanId: string;
  flags: string;
};

/**
 * Random lowercase hex string of the given length. Isomorphic: prefers the Web Crypto API
 * (present in real browsers and Node), and falls back to Math.random in environments that
 * lack it — e.g. some jsdom test setups where globalThis.crypto is incomplete.
 */
export function randomHex(length: number): string {
  const byteCount = Math.ceil(length / 2);
  const bytes = new Uint8Array(byteCount);
  if (typeof globalThis.crypto?.getRandomValues === 'function') {
    globalThis.crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < byteCount; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  let hex = '';
  for (const byte of bytes) {
    hex += byte.toString(16).padStart(2, '0');
  }
  return hex.slice(0, length);
}

/** Nanosecond timestamp rendered as the 16-hex-char prefix of a W3C trace id. */
export function formatTimestampHex(nsTimestamp: bigint): string {
  return nsTimestamp.toString(16).padStart(16, '0').substring(0, 16);
}

/** W3C trace id: 16-hex ns-timestamp prefix + a random suffix (datarouter-compatible). */
export function formatTraceId(nsTimestamp: bigint, random: string): string {
  return formatTimestampHex(nsTimestamp) + random;
}

export function buildTraceparent(traceId: string, spanId: string, flags: string): string {
  return `00-${traceId}-${spanId}-${flags}`;
}

/** W3C trace-flags sampled bit (bit 0); datarouter persists sampled traces (TRACE_CONTEXT reason). */
export const TRACE_FLAG_SAMPLED = 0x01;

/** Return the 2-hex `flags` with the sampled bit set (e.g. `00` -> `01`, `02` -> `03`). Clamped to a
 * single byte so malformed inbound flags can't produce an invalid (>2-hex) W3C traceparent. */
export function enableSampleFlag(flags: string): string {
  const value = parseInt(flags, 16);
  return (((Number.isNaN(value) ? 0 : value) & 0xff) | TRACE_FLAG_SAMPLED).toString(16).padStart(2, '0');
}

export function parseTraceparent(traceparent = ''): Traceparent | undefined {
  const parts = traceparent.toString().split('-');
  if (parts.length === 4 && parts[1].length === TRACE_ID_LENGTH) {
    return { traceId: parts[1], spanId: parts[2], flags: parts[3] };
  }
}

export function parseTracestate(tracestate = ''): Record<string, string> {
  const state: Record<string, string> = {};
  tracestate.split(',').forEach((keyVal) => {
    if (keyVal) {
      const [key, value] = keyVal.split('=');
      if (value !== undefined) {
        state[key.trim()] = value.trim();
      }
    }
  });
  return state;
}

export function formatTracestateMember(key: string, value: string): string {
  key = key.toLowerCase().replace(/[^a-z0-9_\-*/]/g, '');
  value = value.replace(/[,=\s]/g, '');
  return value.length ? `${key}=${value}` : '';
}

export function buildTracestate(tags: Record<string, unknown>): string {
  const members: Array<string> = [];
  Object.keys(tags).forEach((key) => {
    if (tags[key] !== undefined) {
      const member = formatTracestateMember(key, tags[key]?.toString() || '');
      if (member) {
        members.push(member);
      }
    }
  });
  return members.join(',');
}
